import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { commonConstants } from 'constants/index';
import { requestHelpers, userHelpers } from 'helpers';
import authHeaderHelpers from 'helpers/authHeader';
import configHelpers from 'helpers/config';
import trackingHelpers from 'helpers/tracking';
import userServices from 'services/apis/user';
import qs from 'qs';

const { getCommonConfig } = configHelpers;
const { nonBlockTrackRespTimeWithStartAt } = trackingHelpers;
const { apiSellerUrl, apiStaffBffUrl, apiUnsUrl } = getCommonConfig();

const sellerClient = axios.create({
  baseURL: apiSellerUrl,
});

const staffClient = axios.create({
  baseURL: apiStaffBffUrl,
});

const staffClientWithoutError = axios.create({
  baseURL: apiStaffBffUrl,
});

const unsClient = axios.create({
  baseURL: apiUnsUrl,
  paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
});

const getAccessToken = () =>
  userServices.isLoggedIn() ? userServices.getAccessToken() : undefined;

const requestInterceptor = (request: AxiosRequestConfig) => {
  // Add X-Seller-Id
  const currentSeller = userHelpers.getCurrentSeller();
  if (currentSeller) {
    request.headers['X-Seller-Id'] =
      request.headers['X-Seller-Id'] || currentSeller;
  }
  authHeaderHelpers.applyAuthorization(request, getAccessToken());
  request.headers.meta_data = { start_at: Math.round(performance.now()) };
  return request;
};

// Any status code that lie within the range of 2xx cause this function to trigger
const responseSuccessInterceptor = (response: AxiosResponse) => {
  // Do something with response data
  // Call API response time and send tracking data

  const startAt = response.config.headers.meta_data?.start_at;
  const traceId = response.data?.traceId || 'not_available';
  // Check if start_at is exist
  const url = response.config.url || commonConstants.UNDEFINED_URL;

  const { data: payload, method, params, headers } =
    (response.config as AxiosRequestConfig) || {};

  const dataTracking = {
    header: {
      ...headers,
      // Masks the token before tracking, not a token being attached
      // eslint-disable-next-line no-restricted-syntax
      Authorization: !!headers?.Authorization ? 'token' : '',
    },
    payload,
    method,
    params,
    traceId,
    isFulfilled: true,
  };

  nonBlockTrackRespTimeWithStartAt(url, startAt, dataTracking);
  return response;
};

// Any status codes that falls outside the range of 2xx cause this function to trigger
const responseErrorInterceptor = (error: AxiosError) => {
  const startAt = error.response?.config.headers.meta_data?.start_at;
  const url = error.response?.config.url;
  const traceId = error.response?.data?.traceId || 'not_available';

  nonBlockTrackRespTimeWithStartAt(url, startAt, {
    traceId,
    isFulfilled: false,
  });
  // Do something with response error
  requestHelpers.handleResponseError(error);
  return Promise.reject(error);
};

const clients = [sellerClient, staffClient, unsClient];

clients.forEach(client => {
  client.interceptors.request.use(requestInterceptor);
  client.interceptors.response.use(
    responseSuccessInterceptor,
    responseErrorInterceptor
  );
});

staffClientWithoutError.interceptors.request.use(requestInterceptor);
staffClientWithoutError.interceptors.response.use(responseSuccessInterceptor);

export default {
  sellerClient,
  staffClient,
  unsClient,
  staffClientWithoutError,
};
